import { RotateCcw, Copy, FileText, FileJson, Zap, CheckCircle } from 'lucide-react';
import type { DecisionData, UrgencyLevel, CorrectnessLevel, Quadrant } from '../types';

interface ResultsViewProps {
  decisionData: DecisionData;
  urgencyLevel: UrgencyLevel;
  correctnessLevel: CorrectnessLevel;
  onReset: () => void;
}

export function ResultsView({ decisionData, urgencyLevel, correctnessLevel, onReset }: ResultsViewProps) {
  const getQuadrant = (): Quadrant => {
    if (urgencyLevel === 'high' && correctnessLevel === 'high') return 'fast-right';
    if (urgencyLevel === 'high' && correctnessLevel !== 'high') return 'fast';
    if (urgencyLevel !== 'high' && correctnessLevel === 'high') return 'right';
    return 'either';
  };

  const quadrant = getQuadrant();

  const getQuadrantInfo = () => {
    switch (quadrant) {
      case 'fast-right':
        return {
          title: 'Fast & Right',
          description: 'This decision requires both speed and rigor. You need to move quickly while ensuring correctness.',
          color: 'red',
          guidance: 'Consider: Can you parallelize research? Use rapid prototyping? Get quick expert input? Time-box analysis phases.',
        };
      case 'fast':
        return {
          title: 'Fast (Not Right)',
          description: 'Speed is more important than perfect correctness. Make a good-enough decision quickly.',
          color: 'orange',
          guidance: 'Consider: Set a time limit for analysis. Use heuristics or rules of thumb. Accept "good enough" over perfect.',
        };
      case 'right':
        return {
          title: 'Right (Not Fast)',
          description: 'Correctness is more important than speed. Take the time needed to get it right.',
          color: 'blue',
          guidance: 'Consider: Conduct thorough analysis. Gather stakeholder input. Test assumptions. Document rationale.',
        };
      case 'either':
        return {
          title: 'Either (Low Urgency & Low Correctness)',
          description: 'Neither speed nor perfect correctness is critical. Choose the lowest-cost path.',
          color: 'gray',
          guidance: 'Consider: Use the simplest approach. Delegate if appropriate. Don\'t overthink it.',
        };
    }
  };

  const quadrantInfo = getQuadrantInfo();

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]';
      case 'orange':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'blue':
        return 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]';
      case 'gray':
        return 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-pri)]';
      default:
        return 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# Fast vs Right Framework Analysis\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        content += `## Decision\n\n`;
        content += `**${decisionData.decisionName}**\n\n`;
        content += `${decisionData.context}\n\n`;
        content += `### Success Criteria\n${decisionData.successCriteria}\n\n`;
        if (decisionData.constraints) content += `### Constraints\n${decisionData.constraints}\n\n`;
        if (decisionData.deadlines) content += `### Deadlines\n${decisionData.deadlines}\n\n`;
        if (decisionData.stakeholders) content += `### Stakeholders\n${decisionData.stakeholders}\n\n`;
        content += `## Assessment\n\n`;
        content += `- **Urgency:** ${urgencyLevel}\n`;
        content += `- **Correctness Required:** ${correctnessLevel}\n\n`;
        content += `## Result\n\n`;
        content += `**Quadrant:** ${quadrantInfo.title}\n\n`;
        content += `${quadrantInfo.description}\n\n`;
        content += `### Guidance\n\n${quadrantInfo.guidance}\n\n`;
        filename = `fast-vs-right-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = JSON.stringify({
          decision: decisionData,
          assessment: { urgencyLevel, correctnessLevel },
          result: { quadrant, quadrantInfo },
        }, null, 2);
        filename = `fast-vs-right-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = `Fast vs Right Analysis\n\nDecision: ${decisionData.decisionName}\nUrgency: ${urgencyLevel}\nCorrectness: ${correctnessLevel}\n\nResult: ${quadrantInfo.title}\n${quadrantInfo.description}\n\nGuidance: ${quadrantInfo.guidance}`;
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Fast vs Right Analysis
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Decision framework result and guidance
        </p>

        {/* Decision Summary */}
        <div className="mb-6 p-4 bg-[var(--surface)] border border-[var(--rule)]">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">{decisionData.decisionName}</h3>
          <p className="text-sm text-[var(--text-sec)] mb-3">{decisionData.context}</p>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Urgency:</span> <span className="capitalize">{urgencyLevel}</span>
            </div>
            <div>
              <span className="font-medium">Correctness Required:</span> <span className="capitalize">{correctnessLevel}</span>
            </div>
          </div>
        </div>

        {/* Quadrant Result */}
        <div className={`mb-6 p-6  border-2 ${getColorClasses(quadrantInfo.color)}`}>
          <div className="flex items-center gap-3 mb-3">
            <Zap className="h-6 w-6" />
            <h3 className="text-2xl font-bold">{quadrantInfo.title}</h3>
          </div>
          <p className="text-lg mb-4">{quadrantInfo.description}</p>
          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
            <h4 className="font-semibold mb-2">Guidance:</h4>
            <p className="text-sm">{quadrantInfo.guidance}</p>
          </div>
        </div>

        {/* Manager Conversation Guidance */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <div className="flex items-start gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <h4 className="font-semibold text-[var(--text-sec)]">Negotiating Expectations</h4>
          </div>
          <p className="text-sm text-[var(--text-sec)]">
            Here's how to negotiate expectations with your manager: "Based on the urgency ({urgencyLevel}) and correctness requirements ({correctnessLevel}), 
            this decision falls in the '{quadrantInfo.title}' quadrant. This means {quadrantInfo.description.toLowerCase()} 
            We should {quadrantInfo.guidance.toLowerCase()}"
          </p>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


